import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Zap, Loader2, Sparkles, Clock, CreditCard, Gift, ArrowRight, CheckCircle2 } from "lucide-react";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/hooks/useAuth";
import { useOnboarding } from "@/hooks/useOnboarding";
import { PLANS, calculateYearlySavings, formatLimit, type PlanType, type BillingPeriod } from "@/lib/stripe";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { isInFreeTrial, hasHadFreeTrial } from "@/lib/freeTrial";
import { getPricingContext, getPricingTexts } from "@/lib/pricingContext";

/**
 * Page de configuration du paiement
 * Affichée après l'onboarding pour collecter la CB ou le code invité
 */
export default function PaymentSetup() {
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut } = useAuth();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { checkOnboardingCompleted } = useOnboarding();

  const {
    subscribed,
    isTrialing,
    status,
    hasStripeSubscription,
    loading: subscriptionLoading,
    openCheckout,
    refreshSubscription,
  } = useSubscription();

  // Rediriger vers auth si pas connecté
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
    }
  }, [user, authLoading, navigate]);

  // Rediriger vers onboarding si pas complété
  useEffect(() => {
    if (!authLoading && user) {
      const onboardingCompletedInMetadata = user.user_metadata?.onboarding_completed === true;
      const onboardingCompletedInStorage = checkOnboardingCompleted();

      // Si ni les métadonnées ni le localStorage n'indiquent que l'onboarding est complété
      if (!onboardingCompletedInMetadata && !onboardingCompletedInStorage) {
        navigate('/onboarding');
      }
    }
  }, [user, authLoading, checkOnboardingCompleted, navigate]);

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>("monthly");
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState("");
  const [inviteCodeLoading, setInviteCodeLoading] = useState(false);
  const [inviteCodeError, setInviteCodeError] = useState("");

  // Gérer le retour de Stripe Checkout
  useEffect(() => {
    const handleStripeReturn = async () => {
      if (searchParams.get("success") === "true") {
        // Marquer le payment setup comme complété dans les métadonnées
        await supabase.auth.updateUser({
          data: { payment_setup_completed: true }
        });

        // Adapter le message selon que l'utilisateur commence un essai ou s'abonne directement
        const isNewTrial = !hasHadFreeTrial(user) || isInFreeTrial(user);
        toast({
          title: "Paiement configuré !",
          description: isNewTrial
            ? "Votre essai gratuit de 14 jours commence maintenant. Bienvenue dans B8ild !"
            : "Votre abonnement est actif. Bienvenue dans B8ild !",
        });
        await refreshSubscription();
        // Rediriger vers le dashboard
        navigate("/dashboard");
      } else if (searchParams.get("canceled") === "true") {
        toast({
          title: "Configuration annulée",
          description: "Choisissez un plan pour continuer.",
          variant: "destructive",
        });
      }
    };
    handleStripeReturn();
  }, [searchParams, navigate, refreshSubscription, toast]);

  // Rediriger vers dashboard si l'utilisateur a déjà un accès valide
  // SAUF s'il arrive volontairement via ?choose=true (depuis le banner free trial)
  useEffect(() => {
    if (!subscriptionLoading && !authLoading && user) {
      const choosingPlan = searchParams.get("choose") === "true";
      const paymentSetupCompleted = user.user_metadata?.payment_setup_completed === true;
      const hasInvite = user.user_metadata?.has_invite_access === true;
      const freeTrialActive = isInFreeTrial(user);

      // Si l'utilisateur choisit volontairement un plan, ne pas rediriger
      if (choosingPlan) return;

      // Rediriger si accès valide (abonnement Stripe, code invité, ou free trial actif)
      if (paymentSetupCompleted && (subscribed || isTrialing || hasInvite || freeTrialActive)) {
        navigate("/dashboard");
      }
    }
  }, [subscribed, isTrialing, subscriptionLoading, authLoading, user, navigate, searchParams]);

  const handleSubscribe = async (targetPlan: PlanType) => {
    if (targetPlan === "free") return;
    setLoadingPlan(targetPlan);
    try {
      await openCheckout(targetPlan as Exclude<PlanType, "free">, billingPeriod);
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleInviteCode = async () => {
    setInviteCodeError("");

    if (!inviteCode.trim()) {
      setInviteCodeError("Veuillez entrer un code");
      return;
    }

    setInviteCodeLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Session expirée");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-invite-code`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ code: inviteCode.trim() }),
        }
      );

      const result = await response.json();

      if (!response.ok || !result.valid) {
        setInviteCodeError(result.error || "Code invité invalide");
        return;
      }

      toast({
        title: "Code accepté !",
        description: "Bienvenue ! Vous avez un accès complet à B8ild.",
      });

      // Rafraîchir le JWT pour récupérer has_invite_access mis à jour côté serveur
      await supabase.auth.refreshSession();
      await refreshSubscription();
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Erreur code invité:", error);
      if (error?.message?.includes('session') || error?.message?.includes('Session') || error?.name === 'AuthSessionMissingError') {
        setInviteCodeError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate('/auth'), 2000);
      } else {
        setInviteCodeError("Une erreur est survenue. Réessayez.");
      }
    } finally {
      setInviteCodeLoading(false);
    }
  };

  // Déconnexion et retour à la landing page
  const handleLogoClick = async () => {
    await signOut();
    window.location.href = "/";
  };

  // Plans à afficher (sans free)
  const displayPlans = PLANS.filter(p => p.id !== "free");

  // Déterminer le contexte pricing et les textes à afficher
  const pricingContext = useMemo(() => getPricingContext({
    status,
    hasStripeSubscription,
    hasHadTrial: hasHadFreeTrial(user),
    isInTrial: isInFreeTrial(user),
  }), [status, hasStripeSubscription, user]);

  const texts = useMemo(() => {
    const baseTexts = getPricingTexts(pricingContext);
    // Override si l'utilisateur est en free trial et choisit volontairement un plan
    if (isInFreeTrial(user)) {
      return {
        ...baseTexts,
        title: 'Choisissez votre plan',
        subtitle: 'Passez à un plan payant pour continuer après votre essai gratuit.',
        subtitleHighlight: 'Aucun prélèvement pendant l\'essai.',
      };
    }
    return baseTexts;
  }, [pricingContext, user]);

  if (subscriptionLoading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleLogoClick}
              className="text-2xl font-bold text-foreground tracking-tight hover:opacity-80 transition-opacity cursor-pointer"
              title="Retour à l'accueil"
            >
              B<span className="text-[#F59E0B]">8</span>ild
            </button>
            {texts.badge && (
              <Badge className="bg-amber-500 text-black border-0">
                <Clock className="mr-1 h-3 w-3" />
                {texts.badge}
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-6xl">
        {/* Titre — adapté selon le contexte pricing */}
        <div className="text-center mb-12 animate-fade-up">
          <h1 className="text-4xl font-black mb-4">
            {texts.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {texts.subtitle}
            {texts.subtitleHighlight && (
              <>
                <br />
                <span className="text-primary font-medium">{texts.subtitleHighlight}</span>
              </>
            )}
          </p>
          {texts.reassurance && (
            <p className="mt-4 text-base text-green-600 font-medium flex items-center justify-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              {texts.reassurance}
            </p>
          )}
        </div>

        {/* Toggle Mensuel / Annuel */}
        <div className="flex flex-col items-center gap-4 mb-10">
          <div className="flex items-center gap-4 bg-muted/50 rounded-full p-1.5">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={cn(
                "px-6 py-2.5 rounded-full font-bold transition-all",
                billingPeriod === "monthly"
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Mensuel
            </button>
            <button
              onClick={() => setBillingPeriod("yearly")}
              className={cn(
                "px-6 py-2.5 rounded-full font-bold transition-all flex items-center gap-2",
                billingPeriod === "yearly"
                  ? "bg-primary text-primary-foreground shadow-lg"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Annuel
              <Badge className="bg-green-500 text-white text-xs">-20%</Badge>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid gap-6 lg:grid-cols-3 mb-12">
          {displayPlans.map((plan, index) => {
            const price = billingPeriod === "monthly" ? plan.pricing.monthly : plan.pricing.yearly;
            const monthlyEquivalent = plan.pricing.monthlyEquivalent;
            const savings = calculateYearlySavings(plan.id as Exclude<PlanType, "free">);
            const isLoading = loadingPlan === plan.id;

            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative animate-fade-up transition-all hover:shadow-lg",
                  plan.popular && "border-primary shadow-xl shadow-primary/20 scale-[1.02]"
                )}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <CardHeader>
                  {plan.popular && (
                    <Badge className="w-fit mb-3 bg-primary text-primary-foreground font-bold px-3 py-1.5 absolute -top-3 left-1/2 -translate-x-1/2">
                      <Zap className="h-4 w-4 mr-1" />
                      Recommandé
                    </Badge>
                  )}
                  <CardTitle className="text-2xl font-black">{plan.name}</CardTitle>
                  <CardDescription className="text-base">{plan.description}</CardDescription>
                  <div className="mt-4">
                    <span className="text-4xl font-black text-primary">
                      {price}€
                    </span>
                    <span className="text-muted-foreground">
                      {billingPeriod === "monthly" ? " HT /mois" : " HT /an"}
                    </span>
                    {billingPeriod === "yearly" && (
                      <p className="text-sm text-green-500 font-medium mt-1">
                        <Sparkles className="inline h-4 w-4 mr-1" />
                        Soit {monthlyEquivalent}€ HT/mois (-{savings}%)
                      </p>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  <ul className="space-y-3">
                    {plan.features.slice(0, 5).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Limites du plan */}
                  <div className="pt-4 border-t space-y-2 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Chantiers</span>
                      <span className="font-medium text-foreground">{formatLimit(plan.limits.max_chantiers)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Analyses IA/mois</span>
                      <span className="font-medium text-foreground">{formatLimit(plan.limits.max_analyses_ia)}</span>
                    </div>
                  </div>

                  <Button
                    className={cn(
                      "w-full font-bold text-base py-6",
                      plan.popular && "bg-amber-500 hover:bg-amber-600 text-black border-0"
                    )}
                    variant={plan.popular ? "default" : "outline"}
                    size="lg"
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={isLoading || !!loadingPlan}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Redirection...
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-5 w-5" />
                        {texts.buttonLabel}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Section Code Invité */}
        <Card className="max-w-md mx-auto border-dashed">
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mb-3">
              <Gift className="h-6 w-6 text-amber-500" />
            </div>
            <CardTitle className="text-lg">Vous avez un code invité ?</CardTitle>
            <CardDescription>
              Entrez votre code pour accéder gratuitement à B8ild
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="inviteCode">Code invité</Label>
                <Input
                  id="inviteCode"
                  placeholder="Entrez votre code"
                  value={inviteCode}
                  onChange={(e) => {
                    setInviteCode(e.target.value);
                    setInviteCodeError("");
                  }}
                  className={cn(inviteCodeError && "border-red-500")}
                />
                {inviteCodeError && (
                  <p className="text-sm text-red-500">{inviteCodeError}</p>
                )}
              </div>
              <Button
                variant="outline"
                className="w-full font-medium"
                onClick={handleInviteCode}
                disabled={inviteCodeLoading || !inviteCode.trim()}
              >
                {inviteCodeLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  <>
                    Valider le code
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer info */}
        {texts.footer && (
          <div className="text-center mt-12 text-sm text-muted-foreground">
            <p>{texts.footer}</p>
          </div>
        )}
      </div>
    </div>
  );
}
